'use client';

import React from 'react';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { FormattedMessage } from 'react-intl';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { Link } from '@packages/modules/src/Link/Link';
import { buttonVariants } from '@packages/shared/src/ui/Button/Button.variants';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { PaymentImage } from '@packages/shared/src/ui/PaymentImage/PaymentImage';

interface Props {
  url: string;
  amount: {
    value: number;
    currencyCode: string;
  };
  className?: string;
}

export const PaylinkCard = ({ url, amount, className }: Props) => {
  const {
    order: {
      paylink: { enabled, paymentMethods },
    },
  } = useConfig();
  const { formatPrice } = useFormatPrice();

  if (!enabled) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader hasDivider>
        <h3 className="text-size-headline-base">
          <FormattedMessage
            id="PaylinkCard.Headline"
            defaultMessage="Sofort offenen Betrag begleichen"
          />
        </h3>
      </CardHeader>
      <CardContent className="text-size-body-sm flex justify-between">
        <div>
          <FormattedMessage
            id="PaylinkCard.dueAmountText"
            defaultMessage="Dein offener Betrag{break}<strong>{price}</strong>"
            values={{
              strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              break: <br />,
              price: formatPrice(amount.value, amount.currencyCode),
            }}
          />
        </div>
        <Link
          href={url}
          className={buttonVariants({
            size: 'medium',
            variant: 'color-button',
            color: 'primary',
          })}
        >
          <FormattedMessage id="PaylinkCard.PayButton" defaultMessage="Jetzt zahlen" />
        </Link>
      </CardContent>
      <CardContent className="text-size-body-sm">
        <Divider className="h-0" />
        <div className="mt-1 font-bold">
          <FormattedMessage
            id="PaylinkCard.infoText"
            defaultMessage="Jetzt schnell, einfach & direkt mit folgenden Optionen begleichen"
          />
        </div>
        <div className="my-2 flex flex-wrap items-center gap-2">
          {paymentMethods.map((method) => (
            <PaymentImage key={method} option={method} />
          ))}
          <span>
            <FormattedMessage
              id="PaylinkCard.OrViaBankTransfer"
              defaultMessage="oder per Banküberweisung"
            />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
